import { keyFromSelector } from "i18next";
import type { TranslationKey } from "../state/i18n";

export const getWeekDays = (date: Date) => {
  const start = new Date(date);
  const day = start.getDay(); // 0 (Sun) - 6 (Sat)
  const mondayOffset = day === 0 ? 6 : day - 1; // Convert to Monday-based week
  start.setDate(start.getDate() - mondayOffset); // Go back to Monday

  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(start);
    day.setDate(start.getDate() + i);
    return day;
  });
};

// https://en.wikipedia.org/wiki/ISO_week_date#Algorithms
export const getWeekNumber = (date: Date): number => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  // Set to Thursday of this week (ISO week date)
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  // January 4 is always in week 1
  const week1 = new Date(d.getFullYear(), 0, 4);
  // Calculate the week number
  return 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
};

export const daysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();

export const MONTH_TRANSLATION_KEYS: Record<number, TranslationKey> = {
  0: keyFromSelector(($) => $.MONTH_JANUARY),
  1: keyFromSelector(($) => $.MONTH_FEBRUARY),
  2: keyFromSelector(($) => $.MONTH_MARCH),
  3: keyFromSelector(($) => $.MONTH_APRIL),
  4: keyFromSelector(($) => $.MONTH_MAY),
  5: keyFromSelector(($) => $.MONTH_JUNE),
  6: keyFromSelector(($) => $.MONTH_JULY),
  7: keyFromSelector(($) => $.MONTH_AUGUST),
  8: keyFromSelector(($) => $.MONTH_SEPTEMBER),
  9: keyFromSelector(($) => $.MONTH_OCTOBER),
  10: keyFromSelector(($) => $.MONTH_NOVEMBER),
  11: keyFromSelector(($) => $.MONTH_DECEMBER),
};

export const MONTH_ABBREVIATED_TRANSLATION_KEYS: Record<number, TranslationKey> = {
  0: keyFromSelector(($) => $.MONTH_JANUARY_ABBREVIATION),
  1: keyFromSelector(($) => $.MONTH_FEBRUARY_ABBREVIATION),
  2: keyFromSelector(($) => $.MONTH_MARCH_ABBREVIATION),
  3: keyFromSelector(($) => $.MONTH_APRIL_ABBREVIATION),
  4: keyFromSelector(($) => $.MONTH_MAY_ABBREVIATION),
  5: keyFromSelector(($) => $.MONTH_JUNE_ABBREVIATION),
  6: keyFromSelector(($) => $.MONTH_JULY_ABBREVIATION),
  7: keyFromSelector(($) => $.MONTH_AUGUST_ABBREVIATION),
  8: keyFromSelector(($) => $.MONTH_SEPTEMBER_ABBREVIATION),
  9: keyFromSelector(($) => $.MONTH_OCTOBER_ABBREVIATION),
  10: keyFromSelector(($) => $.MONTH_NOVEMBER_ABBREVIATION),
  11: keyFromSelector(($) => $.MONTH_DECEMBER_ABBREVIATION),
};

export const WEEKDAY_TRANSLATION_KEYS: Record<number, TranslationKey> = {
  0: keyFromSelector(($) => $.WEEKDAY_SUNDAY_ABBREVIATION),
  1: keyFromSelector(($) => $.WEEKDAY_MONDAY_ABBREVIATION),
  2: keyFromSelector(($) => $.WEEKDAY_TUESDAY_ABBREVIATION),
  3: keyFromSelector(($) => $.WEEKDAY_WEDNESDAY_ABBREVIATION),
  4: keyFromSelector(($) => $.WEEKDAY_THURSDAY_ABBREVIATION),
  5: keyFromSelector(($) => $.WEEKDAY_FRIDAY_ABBREVIATION),
  6: keyFromSelector(($) => $.WEEKDAY_SATURDAY_ABBREVIATION),
};

export const toTimestamp = (dateString: string): number => {
  const timestamp = new Date(dateString).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

export const daysUntil = (input: Date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(input);
  target.setHours(0, 0, 0, 0);

  const diffMs = target.getTime() - today.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
};

export const isValidDate = (date: Date): boolean => !Number.isNaN(date.getTime());

export const parseDateOnly = (input: string): Date => {
  const [year, month, day] = input.split("-").map(Number);
  return new Date(year, month - 1, day);
};
